'use client';

import { ReviewEntity } from '@/entities/review.entity';
import { createContext, PropsWithChildren, useContext } from 'react';
import { ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { useReviewsPageProviderValue } from '@/context/use-reviews-page-provider-value';
import {
    DEFAULT_REVIEWS_PAGE_CONTEXT_VALUE,
    pickReviewsPageDataBindings,
    pickReviewsPageProgressBindings,
    pickReviewsPageQueryBindings,
    pickReviewsPageSelectionBindings,
    pickReviewsPageTransportBindings,
    ReviewsPageContextType,
} from '@/context/reviews-page.contract';

type Props = PropsWithChildren & {
    totalReviews: number;
    reviews: ReviewEntity[];
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    filters: ReviewSearchParamsDto;
    reviewsOnPage: number;
    maxSelectedItems: number | null;
};

export type {
    AiSelectedAnalysisProgress,
    ReviewsAutoReplyProgress,
    ReviewsExportProgress,
} from '@/context/reviews-page.types';

export type {
    ReviewsPageContextType,
    ReviewsPageContextBindings,
    ReviewsPageSelectionBindings,
    ReviewsPageDataBindings,
    ReviewsPageQueryBindings,
    ReviewsPageTransportBindings,
    ReviewsPageProgressBindings,
} from '@/context/reviews-page.contract';

export const ReviewsPageContext = createContext<ReviewsPageContextType>(DEFAULT_REVIEWS_PAGE_CONTEXT_VALUE);

export const useReviewsPageSelectionContext = () => {
    const value = useContext(ReviewsPageContext);
    return pickReviewsPageSelectionBindings(value);
};

export const useReviewsPageDataContext = () => {
    const value = useContext(ReviewsPageContext);
    return pickReviewsPageDataBindings(value);
};

export const useReviewsPageQueryContext = () => {
    const value = useContext(ReviewsPageContext);
    return pickReviewsPageQueryBindings(value);
};

export const useReviewsPageTransportContext = () => {
    const value = useContext(ReviewsPageContext);
    return pickReviewsPageTransportBindings(value);
};

export const useReviewsPageProgressContext = () => {
    const value = useContext(ReviewsPageContext);
    return pickReviewsPageProgressBindings(value);
};

export const ReviewsPageProvider = ({ children, reviewsOnPage: initialReviewsOnPage, ...props }: Props) => {
    const value = useReviewsPageProviderValue({
        totalReviews: props.totalReviews,
        reviews: props.reviews,
        products: props.products,
        brandGroups: props.brandGroups,
        filters: props.filters,
        reviewsOnPage: initialReviewsOnPage,
        maxSelectedItems: props.maxSelectedItems,
    });

    return <ReviewsPageContext.Provider value={value}>{children}</ReviewsPageContext.Provider>;
};
